import React, { useState } from 'react';
import cn from 'classnames';
import ModalRight from '../../../../../../SharedComponents/ModalRight/ModalRight';
import CloseIcon from '../../../../../../SharedComponents/svg/Icons/CloseIcon';
import styles from './ClientTiktokAudioModal.module.css';
import MetricCardWithChart from '../../../../../../ui/DataDisplay/Statistics/MetricCardWithChart/MetricCardWithChart';
import MusicNoteIcon from '../../../../../../SharedComponents/svg/Icons/MusicNoteIcon';
import CalendarIcon from '../../../../../../SharedComponents/svg/Icons/CalendarIcon';
import {
    buildTiktokMusicUrl,
    formatNumberToCommaNotation,
    formatNumberToKNotation,
    numberWithCommas,
    roundTo2Dp,
} from '../../../../../../helpers';
import ViewIcon from '../../../../../../SharedComponents/svg/Icons/ViewIcon';
import LineChart, { Point } from '../../../../../../ui/DataDisplay/Charts/LineChart/LineChart';
import { ClientExploreTiktokAudiosTableRow, ClientTiktokAudiosTableRow } from '../../../../TikTok.types';
import AudioPlaybackThumbnail from '../../../../../../ui/AudioPlaybackThumbnail/AudioPlaybackThumbnail';
import Tabs, { TabNav, TabNavItem, TabPanel, TabPanels } from '../../../../../../ui/DataDisplay/Tabs/Tabs';
import { useClientTopPostsData } from './ClientTopPostsTable/useClientTopPostsData';
import ClientTopPostsTable from './ClientTopPostsTable/ClientTopPostsTable';
import { ProgressBar, Skeleton } from '@round/ui-kit';
import { ClientTiktokTopVideo } from '@round/api';
import { OrderByParam } from '../../../../../../Hooks/useReactTableSortToOrderBy';
import useAbortableEffect from '../../../../../../Hooks/useAbortableEffect';
import { useClientTiktokAudioModalData } from './useClientTiktokAudioModalData';
import { useClientTiktokAudioViewStats } from './useClientTiktokAudioViewStats';
import countries from '../../../../../../utility/countries';

type ClientTiktokAudioModalProps = {
    isModalOpen: boolean;
    closeModal: () => void;
    audioId: number | undefined;
    clientId: number;
    audioStatsTableRow: ClientTiktokAudiosTableRow | ClientExploreTiktokAudiosTableRow | null;
    modalClassName?: string;
    renderCloseButton?: (close: () => void) => React.ReactNode;
};

const now = new Date();
now.setHours(23, 59, 59);
const twoWeeksAgo = new Date(now.getTime() - 1000 * 60 * 60 * 24 * 7 * 2);
twoWeeksAgo.setHours(0, 0, 0, 0);

const renderTooltip = ({ total, daily }: { total?: number; daily?: number }) => {
    return (
        <div className={styles.tooltipContent}>
            <span className={styles.tooltipDot} />
            <div>
                {typeof total === 'number' && (
                    <p>
                        Total Creations:
                        <span className={styles.tooltipValue}>{formatNumberToCommaNotation(total)}</span>
                    </p>
                )}
                {typeof daily === 'number' && (
                    <p>
                        Daily Creations:
                        <span className={styles.tooltipValue}>{formatNumberToCommaNotation(daily)}</span>
                    </p>
                )}
            </div>
        </div>
    );
};

const ClientTiktokAudioModal = ({
    isModalOpen,
    closeModal,
    audioStatsTableRow,
    audioId,
    clientId,
    modalClassName,
    renderCloseButton = (close) => (
        <button className={styles.closeButton} title="close modal" onClick={close}>
            <CloseIcon width={16} height={16} />
        </button>
    ),
}: ClientTiktokAudioModalProps) => {
    const [activeTab, setActiveTab] = useState('totalCreations');

    const {
        tiktokAudio,
        tiktokAudioStats: fetchedTiktokAudioStats,
        isLoading: isAudioDataLoading,
        isInitialized,
        init,
        reset,
    } = useClientTiktokAudioModalData({
        audioId,
        clientId,
    });

    useAbortableEffect(
        (signal) => {
            if (!audioStatsTableRow && audioId && !isInitialized) {
                init(signal);
            }
        },
        [audioId, audioStatsTableRow, isInitialized, init]
    );

    const {
        viewStats,
        isLoading: isViewStatsLoading,
        reset: resetViewStats,
        isInitialized: isViewStatsInitialized,
        init: initViewStats,
    } = useClientTiktokAudioViewStats({
        audioId,
        clientId,
    });

    useAbortableEffect(
        (signal) => {
            if (clientId && audioId && !isViewStatsInitialized) {
                initViewStats({ signal });
            }
        },
        [audioId, clientId, isViewStatsInitialized, initViewStats]
    );

    const totalViews = viewStats?.total_views || null;
    const totalViewsPostCount = viewStats?.total_views_post_count || null;
    const totalViewsPostCountLabel = totalViewsPostCount ? `Based on ${totalViewsPostCount} posts` : undefined;

    const topLocations = !!viewStats?.locations.countries.length ? viewStats?.locations.countries : null;
    const topLocationsPostCount = viewStats?.location_post_count || 100;
    const topLocationsPostCountLabel = topLocationsPostCount ? `Based on ${topLocationsPostCount} posts` : undefined;

    const audioStats = audioStatsTableRow || fetchedTiktokAudioStats;

    const audioImage = audioStatsTableRow?.image_url || tiktokAudio?.cover_thumb;
    const audioPlayUrl = audioStatsTableRow?.audioUrl || tiktokAudio?.play_url;
    const audioTiktokId = audioStats?.tiktok_id;
    const audioTitle = audioStats?.title;
    const isOriginalAudio = audioStats?.is_original;

    const [statsDateFrom, setStatsDateFrom] = useState<Date>(twoWeeksAgo);
    const [statsDateTo, setStatsDateTo] = useState<Date>(now);

    const statsInRange =
        audioStats?.standardised_data.daily_video_counts.filter((stats) => {
            const date = Date.parse(stats.timestamp);
            return date >= statsDateFrom.getTime() && date <= statsDateTo.getTime();
        }) ?? [];

    const totalCreationsPoints = statsInRange.map((s) => ({
        x: s.timestamp,
        y: s.video_count,
    }));

    const dailyCreationsPoints = statsInRange.map((s) => ({
        x: s.timestamp,
        y: s.change,
    }));

    const totalCreations = audioStats?.video_count;

    const dailyCreations = audioStats?.video_count_daily_change;

    const renderTotalCreationsTooltip = (point: Point) => {
        const dailyCreations = dailyCreationsPoints.find((d) => d.x === point.x);
        return renderTooltip({ total: point.y, daily: dailyCreations?.y });
    };

    const renderDailyCreationsTooltip = (point: Point) => {
        const totalCreations = totalCreationsPoints.find((t) => t.x === point.x);
        return renderTooltip({ total: totalCreations?.y, daily: point.y });
    };

    const [postStatsPage, setPostStatsPage] = useState(1);
    const [postStatsPageSize, setPostStatsPageSize] = useState(10);
    const [postStatsOrderBy, setPostStatsOrderBy] = useState<OrderByParam<ClientTiktokTopVideo>>({
        order_by_desc: 'play_count',
    });

    const {
        postStats,
        isLoading: isPostStatsLoading,
        hasError: postStatsHasError,
        count: postStatsCount,
    } = useClientTopPostsData({
        audioId: audioStats?.audio_id,
        clientId: clientId,
        page: postStatsPage,
        page_size: postStatsPageSize,
        order_by_desc: postStatsOrderBy.order_by_desc,
        order_by_asc: postStatsOrderBy.order_by_asc,
    });

    const topPostsRecordsShowingLabel = `displaying ${postStats.length} post${
        postStats.length === 1 ? '' : 's'
    } out of ${numberWithCommas(postStatsCount)} analysed`;

    const handleClose = () => {
        setStatsDateFrom(twoWeeksAgo);
        setStatsDateTo(now);

        closeModal();
        reset();
        resetViewStats();
    };

    const isAudioPlaybackLoading = !audioPlayUrl && isAudioDataLoading;
    const isStatsLoading = !audioStatsTableRow && isAudioDataLoading;

    return (
        <ModalRight isModalOpen={isModalOpen} closeModal={handleClose} className={cn(styles.modal, modalClassName)}>
            <article className={styles.container}>
                {renderCloseButton(handleClose)}
                <header className={styles.header}>
                    <AudioPlaybackThumbnail
                        className={styles.audioCover}
                        audioUrl={audioPlayUrl}
                        imageUrl={audioImage}
                        loading={isAudioPlaybackLoading}
                    />

                    <div className={styles.audioDetails}>
                        <div className={styles.titleContainer}>
                            <a
                                className={styles.title}
                                href={buildTiktokMusicUrl(audioTitle ?? 'original-sound', audioTiktokId ?? '')}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                {audioTitle}
                            </a>
                        </div>
                        {typeof isOriginalAudio === 'boolean' && (
                            <span
                                className={cn(styles.badge, {
                                    [styles.original]: isOriginalAudio,
                                    [styles.official]: !isOriginalAudio,
                                })}
                            >
                                {isOriginalAudio ? 'Original' : 'Official'}
                            </span>
                        )}
                    </div>
                </header>

                <div data-test-id="headline-stats" className={styles.statsContainer}>
                    <MetricCardWithChart
                        baseColor="#4CC8D9"
                        value={totalCreations}
                        label="Total creations"
                        Icon={MusicNoteIcon}
                        loading={isStatsLoading}
                    />

                    <MetricCardWithChart
                        baseColor="#0090FF"
                        value={dailyCreations}
                        label="Daily creations"
                        Icon={CalendarIcon}
                        format={(value) => `${value > 0 ? '+' : ''}${formatNumberToKNotation(value)}`}
                        loading={isStatsLoading}
                    />

                    <MetricCardWithChart
                        loading={isViewStatsLoading}
                        baseColor="#48D98A"
                        value={totalViews}
                        subtitle={totalViewsPostCountLabel}
                        label="Total views (est.)"
                        Icon={ViewIcon}
                    />
                </div>

                <section className={styles.chartsContainer}>
                    <Tabs activeTab={activeTab} onChange={setActiveTab}>
                        <TabNav>
                            <TabNavItem name="totalCreations">Total creations</TabNavItem>
                            <TabNavItem name="dailyCreations">Daily creations</TabNavItem>
                        </TabNav>

                        <TabPanels>
                            <TabPanel name="totalCreations">
                                <LineChart
                                    enableDateRange
                                    title="Total creations"
                                    points={totalCreationsPoints}
                                    dateFrom={statsDateFrom}
                                    onDateFromChange={setStatsDateFrom}
                                    dateTo={statsDateTo}
                                    onDateToChange={setStatsDateTo}
                                    minDate={twoWeeksAgo}
                                    renderTooltip={renderTotalCreationsTooltip}
                                    loading={isStatsLoading}
                                />
                            </TabPanel>

                            <TabPanel name="dailyCreations">
                                <LineChart
                                    enableDateRange
                                    beginAtZero
                                    title="Daily creations"
                                    points={dailyCreationsPoints}
                                    dateFrom={statsDateFrom}
                                    onDateFromChange={setStatsDateFrom}
                                    dateTo={statsDateTo}
                                    onDateToChange={setStatsDateTo}
                                    minDate={twoWeeksAgo}
                                    renderTooltip={renderDailyCreationsTooltip}
                                    loading={isStatsLoading}
                                />
                            </TabPanel>
                        </TabPanels>
                    </Tabs>
                </section>

                {topLocations && (
                    <section>
                        <div className={styles.sectionHeader}>
                            <p className={styles.sectionTitle}>Top Locations</p>
                            <p className={styles.sectionSubtitle}>{topLocationsPostCountLabel}</p>
                        </div>
                        <div className={styles.topLocationsContainer}>
                            {topLocations
                                .sort((a, b) => b.percentage - a.percentage)
                                .map((country) => (
                                    <div key={country.code} className={styles.topLocation}>
                                        <div className={styles.locationLabel}>
                                            <span
                                                className={cn(`fi fi-${country.code.toLowerCase()}`, styles.flagIcon)}
                                            />
                                            <span className={styles.countryName}>{countries[country.code]}</span>
                                            <span>{numberWithCommas(roundTo2Dp(country.percentage * 100))}%</span>
                                        </div>

                                        <ProgressBar
                                            barStyles={{ backgroundColor: 'var(--color-primary)' }}
                                            progress={roundTo2Dp(country.percentage * 100)}
                                        />
                                    </div>
                                ))}
                        </div>
                    </section>
                )}

                <section>
                    <div className={styles.sectionHeader}>
                        <p className={styles.sectionTitle}>Top posts</p>
                        <p className={styles.sectionSubtitle}>
                            {isPostStatsLoading ? <Skeleton width="20rem" /> : topPostsRecordsShowingLabel}
                        </p>
                    </div>
                    <ClientTopPostsTable
                        loading={isPostStatsLoading}
                        rows={postStats}
                        page={postStatsPage}
                        pageSize={postStatsPageSize}
                        count={postStatsCount}
                        onPageChange={setPostStatsPage}
                        onPageSizeChange={(pageSize) => setPostStatsPageSize(pageSize ?? 10)}
                        orderBy={postStatsOrderBy}
                        onOrderByChange={setPostStatsOrderBy}
                        noDataLabel={
                            postStatsHasError ? 'Sorry, we are experiencing technical issues' : 'No top posts found'
                        }
                    />
                </section>
            </article>
        </ModalRight>
    );
};

export default ClientTiktokAudioModal;
